import { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Combobox,
  Dropdown,
  Field,
  Input,
  Option,
  Subtitle1,
  Subtitle2,
  Switch,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { ArrowUploadRegular, DismissRegular, SaveRegular, SendRegular } from '@fluentui/react-icons';

import { FoldersNavDrawer, TFolder, TPath } from '@modules/items';
import { SignInContext } from '@modules/users';
import { FileDropZone, Path } from '@shared/components';
import { ToastContext } from '@shared/contexts';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';
import { localDateToYMD } from '@shared/utils';

import {
  CHANGE_COMPONENT_OPTIONS,
  CHANGE_MOCK_CURRENT_DATE,
  CHANGE_STATUS,
  CHANGE_TRIGGER_OPTIONS,
} from '../constants';
import { formatMockDate, getChangeDashboardPath, getChangeDetailPath, getNextChangeNumber } from '../helpers';
import type { TChangeDashboardScope, TChangeDetailLocationState, TChangeMockConfig } from '../types';
import ChangeStatusBadge from './ChangeStatusBadge';

type TProps = {
  folder: TFolder;
  fullPath: TPath;
  config: TChangeMockConfig;
  scope: TChangeDashboardScope;
};

const useStyles = makeStyles({
  content: {
    maxWidth: '1120px',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 320px',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'start',
    '@media (max-width: 980px)': {
      gridTemplateColumns: '1fr',
    },
  },
  surface: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingBottom: tokens.spacingVerticalS,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  textarea: {
    minHeight: '112px',
  },
  metaPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    '@media (max-width: 980px)': {
      maxWidth: '520px',
    },
  },
  metaRows: {
    display: 'grid',
    gap: tokens.spacingVerticalL,
  },
  metaRow: {
    display: 'grid',
    gap: tokens.spacingVerticalXXS,
  },
  initiatorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  formFooter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  submitAction: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: tokens.spacingVerticalXS,
  },
  uploadField: {
    '& .fui-Field__validationMessage': {
      overflowWrap: 'anywhere',
    },
  },
  fileList: {
    display: 'grid',
    gap: tokens.spacingVerticalXS,
    marginTop: tokens.spacingVerticalS,
  },
  selectedFile: {
    color: tokens.colorNeutralForeground3,
    overflowWrap: 'anywhere',
  },
});

const getMockDateValue = () => CHANGE_MOCK_CURRENT_DATE.toISOString().slice(0, 10);

const CreateChangeRequestScreen = ({ folder, fullPath, config, scope }: TProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { show } = useContext(ToastContext);
  const { user } = useContext(SignInContext);
  const dashboardPath = getChangeDashboardPath(scope === 'product' ? folder._id : undefined);
  const productLabel = scope === 'product' ? folder.name : 'Current product';
  const initiatorLabel = user.name || user.mail;
  const createdDate = getMockDateValue();
  const [withRegAiAssessment, setWithRegAiAssessment] = useState(true);
  const [changeTitle, setChangeTitle] = useState('');
  const [trigger, setTrigger] = useState('');
  const [description, setDescription] = useState('');
  const [targetDate, setTargetDate] = useState<Date | null>(null);
  const [affectedComponent, setAffectedComponent] = useState('');
  const [proposedImplementation, setProposedImplementation] = useState('');
  const [supportingEvidence, setSupportingEvidence] = useState<string[]>([]);
  const [additionalComments, setAdditionalComments] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const handleMockAction = (message: string) => {
    show('info', message);
  };

  const handleSubmit = () => {
    setAttemptedSubmit(true);

    if (!changeTitle.trim() || !trigger || !description.trim()) {
      show('error', 'Complete the required fields before submitting the change request.');
      return;
    }

    const changeNumber = getNextChangeNumber(config.changes);
    const detailState: TChangeDetailLocationState = {
      changeRequest: {
        changeNumber,
        changeTitle: changeTitle.trim(),
        product: productLabel,
        owner: initiatorLabel,
        targetDate: targetDate ? localDateToYMD(targetDate) : undefined,
        trigger,
        description: description.trim(),
        affectedComponent: affectedComponent.trim() || undefined,
        proposedImplementation: proposedImplementation.trim() || undefined,
        supportingEvidence,
        additionalComments: additionalComments.trim() || undefined,
        createdDate,
      },
      startAiAssessment: withRegAiAssessment && (config.startAiAssessment ?? true),
    };

    navigate(getChangeDetailPath(changeNumber, scope === 'product' ? folder._id : undefined), { state: detailState });
  };

  return (
    <div className="flex flex-1 h-full">
      <FoldersNavDrawer folder={folder} fullPath={fullPath} selectedValue="changes" />

      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className={mergeClasses('flex flex-col gap-5 pt-4 pb-16 pl-5 pr-8', classes.content)}>
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />

            <div className={classes.headerRow}>
              <div className="min-w-0">
                <Subtitle1>Create change request</Subtitle1>
              </div>

              <Link to={dashboardPath}>
                <Button appearance="secondary" icon={<DismissRegular />}>
                  Cancel
                </Button>
              </Link>
            </div>

            <div className={classes.layout}>
              <div className={classes.surface}>
                <section className={classes.section}>
                  <div className={classes.sectionHeader}>
                    <Subtitle2>Required information</Subtitle2>
                  </div>

                  <div className={classes.grid}>
                    <Field
                      label="Change title"
                      required
                      validationState={attemptedSubmit && !changeTitle.trim() ? 'error' : undefined}
                    >
                      <Input
                        placeholder="Short descriptive title"
                        value={changeTitle}
                        onChange={(_, data) => setChangeTitle(data.value)}
                      />
                    </Field>

                    <Field label="Trigger" required validationState={attemptedSubmit && !trigger ? 'error' : undefined}>
                      <Dropdown
                        placeholder="Select trigger"
                        selectedOptions={trigger ? [trigger] : []}
                        value={trigger}
                        onOptionSelect={(_, data) => setTrigger((data.optionValue as string | undefined) ?? '')}
                      >
                        {CHANGE_TRIGGER_OPTIONS.map((option) => (
                          <Option key={option} value={option}>
                            {option}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>

                    <Field
                      label="Change description"
                      required
                      className={classes.fullWidth}
                      validationState={attemptedSubmit && !description.trim() ? 'error' : undefined}
                    >
                      <Textarea
                        resize="vertical"
                        className={classes.textarea}
                        placeholder="Describe what will change and what is expected to remain unchanged."
                        value={description}
                        onChange={(_, data) => setDescription(data.value)}
                      />
                    </Field>
                  </div>
                </section>

                <section className={classes.section}>
                  <div className={classes.sectionHeader}>
                    <Subtitle2>Optional context</Subtitle2>
                  </div>

                  <div className={classes.grid}>
                    <Field label="Target date">
                      <DatePicker
                        placeholder="Optional target date"
                        firstDayOfWeek={1}
                        value={targetDate}
                        onSelectDate={(date) => setTargetDate(date ?? null)}
                        formatDate={(date) => (date ? formatMockDate(localDateToYMD(date)) : '')}
                      />
                    </Field>
                    <Field label="Affected component or document" className={classes.fullWidth}>
                      <Combobox
                        placeholder="Search or select component or document"
                        selectedOptions={affectedComponent ? [affectedComponent] : []}
                        value={affectedComponent}
                        onChange={(event) => setAffectedComponent(event.target.value)}
                        onOptionSelect={(_, data) =>
                          setAffectedComponent((data.optionValue as string | undefined) ?? '')
                        }
                      >
                        {CHANGE_COMPONENT_OPTIONS.map((option) => (
                          <Option key={option} value={option}>
                            {option}
                          </Option>
                        ))}
                      </Combobox>
                    </Field>

                    <Field label="Proposed implementation" className={classes.fullWidth}>
                      <Textarea
                        resize="vertical"
                        className={classes.textarea}
                        placeholder="Optional implementation proposal"
                        value={proposedImplementation}
                        onChange={(_, data) => setProposedImplementation(data.value)}
                      />
                    </Field>

                    <Field label="Supporting evidence" className={mergeClasses(classes.fullWidth, classes.uploadField)}>
                      <FileDropZone
                        icon={<ArrowUploadRegular fontSize={32} />}
                        label="Drop files here, or click to browse"
                        hint="PDF, Word, Excel, images, or drawings"
                        multiple
                        onFiles={(files) => setSupportingEvidence(files.map((file) => file.name))}
                      />
                      {supportingEvidence.length > 0 && (
                        <div className={classes.fileList}>
                          {supportingEvidence.map((fileName) => (
                            <Caption1 key={fileName} className={classes.selectedFile}>
                              {fileName}
                            </Caption1>
                          ))}
                        </div>
                      )}
                    </Field>

                    <Field label="Additional comments" className={classes.fullWidth}>
                      <Textarea
                        resize="vertical"
                        placeholder="Optional comments"
                        value={additionalComments}
                        onChange={(_, data) => setAdditionalComments(data.value)}
                      />
                    </Field>
                  </div>
                </section>

                <div className={classes.formFooter}>
                  <Switch
                    checked={withRegAiAssessment}
                    label="Run regulatory assessment"
                    onChange={(_, data) => setWithRegAiAssessment(data.checked)}
                  />
                  <div className={classes.actions}>
                    <Link to={dashboardPath}>
                      <Button appearance="secondary" icon={<DismissRegular />}>
                        Cancel
                      </Button>
                    </Link>
                    <Button
                      appearance="secondary"
                      icon={<SaveRegular />}
                      onClick={() => handleMockAction('Draft saving is unavailable for this demo.')}
                    >
                      Save draft
                    </Button>

                    <Button appearance="primary" icon={<SendRegular />} onClick={handleSubmit}>
                      Submit
                    </Button>
                  </div>
                </div>
              </div>

              <aside className={classes.metaPanel}>
                <Subtitle2>Details</Subtitle2>
                <div className={classes.metaRows}>
                  <div className={classes.metaRow}>
                    <Caption1 className={classes.muted}>Product</Caption1>
                    <Body1Strong>{productLabel}</Body1Strong>
                  </div>
                  <div className={classes.metaRow}>
                    <Caption1 className={classes.muted}>Initiator</Caption1>
                    <div className={classes.initiatorRow}>
                      <Avatar name={initiatorLabel} color="colorful" size={28} />
                      <Body1>{initiatorLabel}</Body1>
                    </div>
                  </div>
                  <div className={classes.metaRow}>
                    <Caption1 className={classes.muted}>Created date</Caption1>
                    <Body1>{formatMockDate(createdDate)}</Body1>
                  </div>
                  <div className={classes.metaRow}>
                    <Caption1 className={classes.muted}>Status</Caption1>
                    <div>
                      <ChangeStatusBadge status={CHANGE_STATUS.DRAFT} />
                    </div>
                  </div>
                  <div className={classes.metaRow}>
                    <Caption1 className={classes.muted}>Change number</Caption1>
                    <Body1>Assigned after saving</Body1>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateChangeRequestScreen;
